import {
  Folder, FileText, FileSpreadsheet, Presentation, FileImage,
  Video, Mic, FileArchive, FileCode, File as FileIcon, FileType,
} from '../../icons';
import { kindOfMime } from '../../lib/file-kind';
import { FileTypeGlyph, type FileGlyphName } from '../../components/FileTypeGlyph';
import type { NodePublic } from '../../api/workspace';

/**
 * 워크스페이스 파일 아이콘 (2026-08-12).
 *
 * 기존 `fileEmoji()` 의 이모지(📁/📝/📊…)를 대체한다. UI 에 이모지를 쓰지 않는 것이 이
 * 프로젝트 규칙이고, 이모지는 OS·폰트마다 모양과 폭이 달라 그리드 정렬이 흔들린다.
 *
 * **2026-08-18 — 그림을 유형별 브랜드 아이콘으로 바꿨다**(`components/FileTypeGlyph`).
 * 선 아이콘 + 색만으로는 Word 와 PDF 가 같은 `FileText` 라 목록에서 구분이 어려웠다.
 * 아래 `pick()` 은 그대로 두고(색 토큰·폴백 경로 보존) 그림만 교체한다 — 유형 판정
 * 로직을 건드리지 않으려는 것이다. 그림이 없는 유형은 지금까지처럼 선 아이콘으로 뜬다.
 *
 * 오피스 3종 판정은 `lib/file-kind.ts` 의 exact MIME 표를 쓴다 — 옛 `includes('document')`
 * 휴리스틱은 pptx(…presentationml.presentation)를 word 로 오분류했다. 그 외(이미지·영상·
 * 음성·압축·코드)는 prefix 로 충분해 여기서만 판정한다.
 */

interface WorkspaceFileIconProps {
  node: Pick<NodePublic, 'type' | 'mimeType'>;
  size?: number;
}

/**
 * 코드로 보여 줄 MIME — 나머지 `text/*` 는 그대로 txt 그림을 쓴다.
 * `application/json` 은 그전에 따로 한 줄로 잡혔는데, 이 표가 앞서 걸러서 그 줄은 없앴다
 * (남겨 두면 절대 안 닿는 줄이 된다).
 */
const CODE_MIMES = new Set([
  'application/json', 'application/javascript', 'application/xml',
  'text/javascript', 'text/css', 'text/xml', 'text/x-python', 'text/x-sh', 'text/x-yaml',
]);

/**
 * 색 토큰 → 브랜드 그림 (2026-08-18).
 *
 * 판정 결과인 `tone` 을 그대로 열쇠로 쓴다 — 유형 판정을 두 벌 두지 않으려는 것이다.
 * 여기 없는 토큰은 `pick()` 이 준 선 아이콘이 그대로 뜬다(폴백 보존).
 */
const GLYPH_BY_TONE: Readonly<Record<string, FileGlyphName>> = {
  folder: 'folder', word: 'word', excel: 'excel', csv: 'csv', ppt: 'ppt', hwp: 'hwp',
  pdf: 'pdf', markup: 'markup', html: 'html', code: 'code', text: 'text',
  image: 'image', video: 'video', audio: 'audio', archive: 'archive', other: 'other',
};

/** 아이콘과 색 토큰을 함께 고른다 — 유형별 시각 구분이 목적. */
function pick(node: Pick<NodePublic, 'type' | 'mimeType'>): { Icon: typeof FileIcon; tone: string } {
  if (node.type === 'folder') return { Icon: Folder, tone: 'folder' };

  const mime = (node.mimeType ?? '').toLowerCase().split(';')[0]!.trim();

  switch (kindOfMime(mime)) {
    case 'word': return { Icon: FileText, tone: 'word' };
    case 'excel': return { Icon: FileSpreadsheet, tone: 'excel' };
    case 'ppt': return { Icon: Presentation, tone: 'ppt' };
    /*
     * 한글 (2026-08-14 2차) — hwp 가 word 에서 떨어져 나오면서 **여기가 반드시 필요해졌다.**
     * 없으면 `application/hwp+zip` 이 아래 `mime.includes('zip')` 에 걸려 압축파일 아이콘이
     * 된다(원래 word 분기에서 잡히던 것이라 조용히 바뀐다).
     */
    case 'hwp': return { Icon: FileType, tone: 'hwp' };
    // 마크다운 (2026-08-18) — 그전에는 아래 `text/` 로 떨어져 txt 와 같은 그림이었다.
    case 'markup': return { Icon: FileCode, tone: 'markup' };
    default: break;
  }

  if (mime.startsWith('image/')) return { Icon: FileImage, tone: 'image' };
  if (mime.startsWith('video/')) return { Icon: Video, tone: 'video' };
  if (mime.startsWith('audio/')) return { Icon: Mic, tone: 'audio' };
  if (mime === 'application/pdf') return { Icon: FileText, tone: 'pdf' };
  /*
   * text/* 안에서 갈라지는 셋 (2026-08-18) — 전부 `text/` 한 줄에 뭉쳐 있던 것이다.
   * 반드시 아래 `startsWith('text/')` **앞**에 와야 한다(먼저 걸리는 쪽이 이긴다).
   * 폴백 선 아이콘은 지금까지와 같은 것을 그대로 쓴다 — 바뀌는 것은 그림뿐.
   */
  if (mime === 'text/csv') return { Icon: FileSpreadsheet, tone: 'csv' };
  if (mime === 'text/html' || mime === 'application/xhtml+xml') return { Icon: FileCode, tone: 'html' };
  if (CODE_MIMES.has(mime)) return { Icon: FileCode, tone: 'code' };
  if (mime.startsWith('text/')) return { Icon: FileCode, tone: 'text' };
  if (mime.includes('zip') || mime.includes('compressed') || mime.includes('tar') || mime.includes('gzip')) {
    return { Icon: FileArchive, tone: 'archive' };
  }
  return { Icon: FileIcon, tone: 'other' };
}

export function WorkspaceFileIcon({ node, size = 22 }: WorkspaceFileIconProps) {
  const { Icon, tone } = pick(node);
  const glyph = GLYPH_BY_TONE[tone];
  return (
    <span className={`workspace-file-icon workspace-file-icon--${tone}`} aria-hidden>
      {glyph ? <FileTypeGlyph name={glyph} size={size} /> : <Icon size={size} />}
    </span>
  );
}
